// src/Header.tsx
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AuthModal } from './AuthModal';
import { isActiveMember } from '@ring/shared/author-trust';
import './Header.css';

interface NavLinkEntry { to: string; label: string; memberOnly?: boolean; }
interface NavGroupEntry { label: string; memberOnly?: boolean; children: NavLinkEntry[]; }
type NavEntry = NavLinkEntry | NavGroupEntry;

function isGroup(entry: NavEntry): entry is NavGroupEntry {
  return 'children' in entry;
}

// memberOnly links are hidden for authors who aren't active members (see
// api/src/authGate.ts's requireActiveAuthor) rather than left visible and
// just erroring once clicked. Groups render as a pill dropdown on desktop.
const NAV_LINKS: NavEntry[] = [
  { to: '/dashboard',         label: 'Dashboard' },
  { to: '/dashboard/library',  label: 'Library',  memberOnly: true },
  { to: '/dashboard/projects', label: 'Projects', memberOnly: true },
  { to: '/dashboard/jobs',     label: 'Renders',  memberOnly: true },
];

export function Header() {
  const { author, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeMember = author ? isActiveMember(author.authorStatus) : false;
  const navLinks: NavEntry[] = [
    ...NAV_LINKS.filter(link => !link.memberOnly || activeMember),
    ...(author?.isAdmin ? [{ to: '/dashboard/admin', label: 'Admin' }] : []),
  ];
  const [showLogin, setShowLogin]   = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false); // desktop profile dropdown (Profile/Sign out)
  const [navMenuOpen, setNavMenuOpen] = useState(false); // mobile hamburger panel — the same NAV_LINKS the pill row shows on desktop
  const [openGroup, setOpenGroup]   = useState<string | null>(null); // label of the open desktop pill dropdown (a nav group), if any
  const menuRef = useRef<HTMLDivElement>(null);
  const navMenuRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!menuOpen && !navMenuOpen && !openGroup) return;
    function handleClick(e: MouseEvent) {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (navMenuOpen && navMenuRef.current && !navMenuRef.current.contains(e.target as Node)) setNavMenuOpen(false);
      if (openGroup) {
        const el = groupRefs.current[openGroup];
        if (el && !el.contains(e.target as Node)) setOpenGroup(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, navMenuOpen, openGroup]);

  async function handleSignOut() {
    setMenuOpen(false);
    await signOut();
    navigate('/');
  }

  return (
    <header className="site-header">
      <div className="site-header-inner">
        <NavLink to={author ? '/dashboard' : '/'} className="site-logo" aria-label="Shelfkin home">
          <span className="site-logo-mark" aria-hidden="true">
            <span className="site-logo-ring site-logo-ring-a" />
            <span className="site-logo-ring site-logo-ring-b" />
          </span>
          Shelfkin
        </NavLink>

        {author && (
          <nav className="site-nav">
            {navLinks.map(entry => isGroup(entry) ? (
              <div className="site-nav-group" ref={el => { groupRefs.current[entry.label] = el; }} key={entry.label}>
                <button
                  className={`site-nav-link site-nav-group-btn${
                    entry.children.some(c => location.pathname.startsWith(c.to)) ? ' site-nav-link-active' : ''
                  }`}
                  onClick={() => setOpenGroup(open => open === entry.label ? null : entry.label)}
                  aria-haspopup="menu"
                  aria-expanded={openGroup === entry.label}
                >
                  {entry.label}
                  <svg
                    className={`site-nav-group-chevron${openGroup === entry.label ? ' site-nav-group-chevron-open' : ''}`}
                    width="9" height="6" viewBox="0 0 9 6" fill="none" aria-hidden="true"
                  >
                    <path d="M1 1L4.5 4.5L8 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {openGroup === entry.label && (
                  <div className="site-profile-menu site-nav-group-menu" role="menu">
                    {entry.children.map(child => (
                      <NavLink key={child.to} role="menuitem" to={child.to} onClick={() => setOpenGroup(null)}>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.to === '/dashboard'}
                className={({ isActive }) => `site-nav-link${isActive ? ' site-nav-link-active' : ''}`}
              >
                {entry.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="site-header-auth">
          {author && (
            <div className="site-nav-toggle" ref={navMenuRef}>
              <button
                className="site-nav-toggle-btn"
                onClick={() => setNavMenuOpen(open => !open)}
                aria-haspopup="menu"
                aria-expanded={navMenuOpen}
                aria-label="Menu"
              >
                <span className="site-nav-toggle-bar" />
                <span className="site-nav-toggle-bar" />
                <span className="site-nav-toggle-bar" />
              </button>
              {navMenuOpen && (
                <div className="site-profile-menu" role="menu">
                  {navLinks.map(entry => isGroup(entry) ? (
                    <div className="site-nav-group-mobile" key={entry.label}>
                      <span className="site-nav-group-mobile-label">{entry.label}</span>
                      {entry.children.map(child => (
                        <NavLink key={child.to} role="menuitem" to={child.to} onClick={() => setNavMenuOpen(false)}>
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : (
                    <NavLink
                      key={entry.to}
                      role="menuitem"
                      to={entry.to}
                      end={entry.to === '/dashboard'}
                      onClick={() => setNavMenuOpen(false)}
                    >
                      {entry.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {author ? (
            <div className="site-profile" ref={menuRef}>
              <button
                className="site-profile-btn"
                onClick={() => setMenuOpen(open => !open)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <span className="site-profile-avatar">{author.email.charAt(0).toUpperCase()}</span>
                {/* Just the part before the @ — the full email heads the menu. */}
                <span className="site-profile-name">{author.email.split('@')[0]}</span>
              </button>
              {menuOpen && (
                <div className="site-profile-menu" role="menu">
                  <div className="site-profile-email" title={author.email}>{author.email}</div>
                  <button role="menuitem" onClick={handleSignOut}>Sign out</button>
                </div>
              )}
            </div>
          ) : (
            <button className="site-login-btn" onClick={() => setShowLogin(true)}>
              Log in
            </button>
          )}
        </div>
      </div>

      {showLogin && <AuthModal onClose={() => setShowLogin(false)} />}
    </header>
  );
}
